import togeojson from '@mapbox/togeojson';
import togpx from 'togpx';
import generateGuid from '../../../util/guid';
import { FeatureNames } from './MapConstants';

const XML_HEADER = '<?xml version="1.0" ?>';

// RouteModel:
// { routes: [geometry], zones: [{id, rings}] }
function parseXml(content) {
    return new DOMParser().parseFromString(content, 'text/xml');
}

function isLineGeometry(geometry) {
    return geometry && (geometry.type === 'LineString' || geometry.type === 'MultiLineString');
}

export function gpxToGeoJson(gpxContent) {
    if (!gpxContent)
        return null;
    return togeojson.gpx(parseXml(gpxContent));
}

export function toRouteModel(gpxContent, markerNames = {}) {
    const geoJson = gpxToGeoJson(gpxContent);
    if (!geoJson)
        return null;

    const zoneName = markerNames.bevoorradingMarkerName || FeatureNames.BEVOORRADINGSZONE;

    const model = { routes: [], zones: [] };
    (geoJson.features || []).forEach(feature => {
        const geometry = feature && feature.geometry;
        if (!geometry)
            return;
        const name = feature.properties && feature.properties.name;

        if (isLineGeometry(geometry)) {
            // Zones staan in GPX als tracks (GPX kent geen polygonen); een LineString is één ring.
            if (name === zoneName)
                model.zones.push({
                    id: generateGuid(),
                    rings: geometry.type === 'LineString' ? [geometry.coordinates] : geometry.coordinates
                });
            else
                model.routes.push(geometry);
        }
    });

    return model;
}

export function toFeatureCollection(routeModel, routeName, markerNames = {}) {
    const features = [];
    if (routeModel) {
        const zoneName = markerNames.bevoorradingMarkerName || FeatureNames.BEVOORRADINGSZONE;

        (routeModel.routes || []).forEach(geometry => features.push({
            type: 'Feature',
            geometry: geometry,
            properties: routeName ? { name: routeName } : {}
        }));
        (routeModel.zones || []).forEach(zone => features.push({
            type: 'Feature',
            geometry: { type: 'Polygon', coordinates: zone.rings },
            properties: { name: zoneName }
        }));
    }
    return { type: 'FeatureCollection', features };
}

export function serializeToGpx(routeModel, routeName, markerNames) {
    const gpx = togpx(toFeatureCollection(routeModel, routeName, markerNames));
    return gpx.indexOf(XML_HEADER) === 0 ? gpx : XML_HEADER + gpx;
}

export function hasRoute(routeModel) {
    return !!(routeModel && routeModel.routes.length);
}

export function reverseRouteDirection(routeModel) {
    if (!routeModel)
        return routeModel;

    const reverseGeometry = geometry => geometry.type === 'LineString'
        ? { ...geometry, coordinates: geometry.coordinates.slice().reverse() }
        : { ...geometry, coordinates: geometry.coordinates.map(line => line.slice().reverse()).reverse() };

    return { ...routeModel, routes: routeModel.routes.slice().reverse().map(reverseGeometry) };
}

export function isValidGpx(gpxContent) {
    const dom = parseXml(gpxContent);

    if (dom === undefined || dom.getElementsByTagName('parsererror').length > 0)
        return false;

    const trackPoints = dom.getElementsByTagName('trkpt');
    for (let i = 0; i < trackPoints.length; i++) {
        const element = trackPoints[i];
        if (!(element.hasAttribute('lon') && element.hasAttribute('lat')))
            return false;
    }
    return true;
}

export function stripWaypoints(gpxContent) {
    const dom = parseXml(gpxContent);
    const waypoints = Array.prototype.slice.call(dom.getElementsByTagName('wpt'));

    waypoints.forEach(element => element.parentNode.removeChild(element));

    return new XMLSerializer().serializeToString(dom);
}

export function routesOnlyGeoJson(gpxContent, excludedNames) {
    const geoJson = gpxToGeoJson(gpxContent);
    if (!geoJson)
        return null;

    const excluded = (excludedNames || []).filter(Boolean);
    geoJson.features = geoJson.features.filter(feature => {
        const name = feature && feature.properties && feature.properties.name;
        return excluded.indexOf(name) === -1;
    });

    return geoJson;
}

export function mapBoundsOfGeoJson(geoJson) {
    if (!geoJson)
        return [];

    let features;
    if (geoJson.features)
        features = geoJson.features;
    else if (geoJson.type === 'Feature')
        features = [geoJson];
    else
        features = [{ geometry: geoJson }];

    const latLngs = [];
    features.forEach(feature => {
        const geometry = feature && feature.geometry;
        if (!isLineGeometry(geometry))
            return;
        if (geometry.type === 'LineString')
            geometry.coordinates.forEach(c => latLngs.push([c[1], c[0]]));
        else
            geometry.coordinates.forEach(line => line.forEach(c => latLngs.push([c[1], c[0]])));
    });

    if (!latLngs.length) {
        features.forEach(feature => {
            const geometry = feature && feature.geometry;
            if (geometry && geometry.type === 'Point')
                latLngs.push([geometry.coordinates[1], geometry.coordinates[0]]);
        });
    }

    return latLngs;
}

export function mapBoundsOfRouteModel(routeModel, routeName, markerNames) {
    return mapBoundsOfGeoJson(toFeatureCollection(routeModel, routeName, markerNames));
}
